import spacetime, { I18nOptions, Spacetime } from "spacetime";

import { DATE_PATTERN } from "src/constants";
import i18n, { Language } from "src/i18n";

const FRENCH_I18N_CONFIG: I18nOptions = {
  days: {
    long: ["Dimanche", "Lundi", "Mardi", "Mercredi", "Jeudi", "Vendredi", "Samedi"],
    short: ["Dim", "Lun", "Mar", "Mer", "Jeu", "Ven", "Sam"],
  },
  months: {
    long: [
      "janvier",
      "février",
      "mars",
      "avril",
      "mai",
      "juin",
      "juillet",
      "août",
      "septembre",
      "octobre",
      "novembre",
      "décembre",
    ],
    short: ["jan", "fév", "mar", "avr", "mai", "jun", "jul", "aoû", "sep", "oct", "nov", "déc"],
  },
  useTitleCase: false, // automatically in .format()
};

const i18nOptions: Record<Language, I18nOptions | undefined> = {
  "fr-FR": FRENCH_I18N_CONFIG,
  "en-GB": undefined,
};

const formatPatterns: Record<Language, Record<DATE_PATTERN, (s: Spacetime) => string>> = {
  "en-GB": {
    DATE: (s: Spacetime) => s.format("numeric-uk"),
    TIME: (s: Spacetime) => s.format("{hour-24}:{minute-pad}"),
    DATE_TIME: (s: Spacetime) => s.format("{numeric-uk}, {hour-24}:{minute-pad}"),
    DAY: (s: Spacetime) => s.format("day"),
    MONTH_YEAR: (s: Spacetime) => s.format("{month} {year}"),
    DAY_DATE: (s: Spacetime) => s.format("{day} {numeric-uk}"),
    ISO_DATE: (s: Spacetime) => s.format("iso-short"),
  },
  "fr-FR": {
    DATE: (s: Spacetime) => s.format("numeric-uk"), // 01/05/2021
    TIME: (s: Spacetime) => s.format("{hour-24}h{minute-pad}"), // 9h20
    DATE_TIME: (s: Spacetime) => s.format("{numeric-uk} à {hour-24}h{minute-pad}"), // 01/05/2021 à 09h20
    DAY: (s: Spacetime) => s.format("day"), // Lundi
    MONTH_YEAR: (s: Spacetime) => s.format("{month} {year}"), // mai 2021
    DAY_DATE: (s: Spacetime) => s.format("{day} {numeric-uk}"), // Mercredi 01/05/2021
    ISO_DATE: (s: Spacetime) => s.format("iso-short"), // 2021-05-01
  },
};

interface FormatDateParams {
  timeZone?: string;
  pattern?: DATE_PATTERN;
}

export const isDate = (value: string | Date) =>
  !!value &&
  (value instanceof Date
    ? !Number.isNaN(value.getTime())
    : !Number.isNaN(new Date(value).getTime()));

export const formatDate = (value: string | Date, params?: FormatDateParams): string | null => {
  const lng = i18n.language as Language;
  const options = i18nOptions[lng];

  const timezone = params?.timeZone ?? Intl.DateTimeFormat().resolvedOptions().timeZone;
  const pattern = params?.pattern ?? "DATE";

  if (!isDate(value)) return null;

  const s = spacetime(value).goto(timezone);

  if (options) s.i18n(options);

  return formatPatterns[lng][pattern](s);
};

export const addDays = (date: string | Date, days: number = 1): string | null => {
  if (!isDate(date)) return null;

  return spacetime(date).add(days, "day").toNativeDate().toISOString();
};

export const addMonths = (date: string | Date, months: number = 1): string | null => {
  if (!isDate(date)) return null;

  return spacetime(date).add(months, "month").toNativeDate().toISOString();
};

export const addYears = (date: string | Date, years: number = 1): string | null => {
  if (!isDate(date)) return null;

  return spacetime(date).add(years, "year").toNativeDate().toISOString();
};

export const isAfterDate = (
  date1: string | Date,
  date2: string | undefined = undefined
): boolean => {
  if (!isDate(date1) || (date2 && !isDate(date2))) return false;

  if (date2) return spacetime(date1).isAfter(spacetime(date2));

  return spacetime(date1).isAfter(spacetime());
};
